import settings from "app/settings";
import axios, { AxiosError } from "axios";

export interface SelfSeatingLayoutObject {
  type?: "table" | "seat" | "other" | string;
  id: string;
  name: string;
  name_en?: string;
  max_person?: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  window?: boolean;
  occupied?: boolean;
  selected?: boolean;
  available?: boolean;
}

export interface SelfSeatingLayout {
  id: number;
  name: string;
  layout: SelfSeatingLayoutObject[];
}

export interface SelfSeatingState {
  booking: {
    id: number;
    booking_number: string;
    name: string;
    email: string;
    language: "hu" | "en";
    guest_count: number;
    window_eligible: boolean;
    table_numbers: string[];
    completed: boolean;
  };
  event: {
    id: number;
    start_time: string;
    program_name: string;
    program_variant_name?: string | null;
  };
  layouts: SelfSeatingLayout[];
  expires_at: string;
}

export interface TableSelection {
  layout_id: number;
  table_id: string;
}

export class SelfSeatingApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly responseData?: unknown,
  ) {
    super("Self seating request failed");
    this.name = "SelfSeatingApiError";
  }
}

const publicApi = axios.create({
  baseURL: settings.apiHost,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: settings.requestTimeout,
});

async function request<T>(operation: () => Promise<{ data: T }>): Promise<T> {
  try {
    const response = await operation();
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new SelfSeatingApiError(
      axiosError.response?.status || 0,
      axiosError.response?.data,
    );
  }
}

export async function identifyBooking(bookingId: number, pin: string) {
  return request<{ token: string; data: SelfSeatingState }>(() =>
    publicApi.post("/self-seating/identify", {
      booking_id: bookingId,
      pin,
    }),
  );
}

export async function loadSelfSeating(token: string) {
  return request<SelfSeatingState>(() =>
    publicApi.get(`/self-seating/${encodeURIComponent(token)}`),
  );
}

export async function saveSelfSeating(token: string, tables: TableSelection[]) {
  return request<SelfSeatingState>(() =>
    publicApi.put(`/self-seating/${encodeURIComponent(token)}/selection`, {
      tables,
    }),
  );
}
